// Dungeon generator, shared by server and client. The client receives {genVersion, seed, theme} and
// builds the SAME geometry; spawns, loot and secrets come from a separate server-only RNG stream, so
// a client cannot map-hack (the Diablo II lesson, research/mmo/dungeons-parties-controls.md).
//
// v1 "rooms and corridors": rooms placed without overlap, connected by a minimum spanning tree over
// their centres plus a couple of loops (players hate pure trees: dead-end backtracking was the D4 beta
// complaint), 2-wide corridors, entrance in the first room, the exit (boss) in the room farthest by path.

export const GEN_VERSION = 1;

export const D_FLOOR = 0, D_WALL = 1, D_WATER = 2, D_EXIT = 3, D_ENTRY = 4;

export interface DRoom { x: number; y: number; w: number; h: number; cx: number; cy: number }
export interface Dungeon {
  w: number; h: number; cells: Uint8Array; rooms: DRoom[];
  entry: { x: number; y: number }; exit: { x: number; y: number }; exitRoom: number; seed: number; theme: string;
}

export function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function generate(seed: number, theme = 'drains', W = 64, H = 48): Dungeon {
  const r = rng(seed ^ 0x9e3779b9);
  const ri = (a: number, b: number) => a + Math.floor(r() * (b - a + 1));
  const cells = new Uint8Array(W * H).fill(D_WALL);
  const rooms: DRoom[] = [];
  const target = ri(9, 12);
  for (let tries = 0; tries < 400 && rooms.length < target; tries++) {
    const w = ri(7, 13), h = ri(6, 10);
    const x = ri(2, W - w - 3), y = ri(3, H - h - 3);
    if (rooms.some(o => x < o.x + o.w + 3 && x + w + 3 > o.x && y < o.y + o.h + 3 && y + h + 3 > o.y)) continue;
    rooms.push({ x, y, w, h, cx: x + (w >> 1), cy: y + (h >> 1) });
  }
  const carve = (x: number, y: number, v = D_FLOOR) => { if (x > 0 && y > 0 && x < W - 1 && y < H - 1) cells[y * W + x] = v; };
  for (const o of rooms) for (let y = o.y; y < o.y + o.h; y++) for (let x = o.x; x < o.x + o.w; x++) carve(x, y);
  // MST (Prim) over room centres + loops
  const n = rooms.length, inTree = [0], edges: [number, number][] = [];
  const d2 = (a: DRoom, b: DRoom) => (a.cx - b.cx) ** 2 + (a.cy - b.cy) ** 2;
  while (inTree.length < n) {
    let best: [number, number, number] | null = null;
    for (const i of inTree) for (let j = 0; j < n; j++) {
      if (inTree.includes(j)) continue;
      const d = d2(rooms[i], rooms[j]);
      if (!best || d < best[2]) best = [i, j, d];
    }
    edges.push([best![0], best![1]]); inTree.push(best![1]);
  }
  for (let k = 0; k < 2 && n > 3; k++) {
    const i = ri(0, n - 1); let j = ri(0, n - 1);
    if (i === j) j = (j + 1) % n;
    if (!edges.some(e => (e[0] === i && e[1] === j) || (e[0] === j && e[1] === i))) edges.push([i, j]);
  }
  const corridor = (a: DRoom, b: DRoom) => {
    const horizFirst = r() < 0.5;
    const hline = (y: number, x0: number, x1: number) => { for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x++) { carve(x, y); carve(x, y + 1); } };
    const vline = (x: number, y0: number, y1: number) => { for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y++) { carve(x, y); carve(x + 1, y); } };
    if (horizFirst) { hline(a.cy, a.cx, b.cx); vline(b.cx, a.cy, b.cy); } else { vline(a.cx, a.cy, b.cy); hline(b.cy, a.cx, b.cx); }
  };
  for (const [i, j] of edges) corridor(rooms[i], rooms[j]);
  // entry in room 0, exit in the room farthest by BFS path
  const start = rooms[0];
  const dist = new Int32Array(W * H).fill(-1);
  const q = [start.cy * W + start.cx]; dist[q[0]] = 0;
  for (let h = 0; h < q.length; h++) {
    const c = q[h];
    for (const dn of [1, -1, W, -W]) { const nb = c + dn; if (cells[nb] !== D_WALL && dist[nb] < 0) { dist[nb] = dist[c] + 1; q.push(nb); } }
  }
  let far = 0, fd = -1;
  rooms.forEach((o, i) => { const d = dist[o.cy * W + o.cx]; if (d > fd) { fd = d; far = i; } });
  const entry = { x: start.cx, y: start.cy }, ex = rooms[far];
  const exit = { x: ex.cx, y: ex.cy };
  cells[entry.y * W + entry.x] = D_ENTRY;
  cells[exit.y * W + exit.x] = D_EXIT;
  return { w: W, h: H, cells, rooms, entry, exit, exitRoom: far, seed, theme };
}

// collision view for the shared mover: only walls block
export function walkMask(d: Dungeon): Uint8Array {
  const m = new Uint8Array(d.cells.length);
  for (let i = 0; i < m.length; i++) m[i] = d.cells[i] === D_WALL ? 1 : 0;
  return m;
}

// Where the drains are entered on the street: the alley behind the safe house. Computed the same way
// on both ends from the manifest and the collision grid, so no extra message is needed.
export function drainEntrance(venues: { key: string; door: [number, number] }[], blocked: (x: number, y: number) => boolean): { x: number; y: number } | null {
  const t = venues.find(v => v.key === 'tavern');
  if (!t) return null;
  const [dx, dy] = t.door;
  for (let r = 0; r < 40; r++) {                       // search outward from ~15 m behind the building
    for (let a = 0; a < 16; a++) {
      const x = dx + Math.cos(a / 16 * Math.PI * 2) * r * 8, y = dy + 240 + Math.sin(a / 16 * Math.PI * 2) * r * 8;
      if (!blocked(x, y) && !blocked(x - 6, y) && !blocked(x + 6, y)) return { x: Math.round(x), y: Math.round(y) };
    }
  }
  return null;
}
